import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { licensingApi } from '@/api/licensing';
import {
  commercialAgreementApi,
  type CommercialAgreement,
} from '@/api/commercialAgreement';
import { Button, Input, Loading, EmptyState } from '@/components/ui';
import { formatNIS } from '@/utils/currency';
import { formatDate } from '@/utils/date';
import { FileText } from 'lucide-react';

interface Props {
  orgId: string;
}

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function toDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDateTimeLocal(value: string): string | null {
  if (!value.trim()) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function CommercialAgreementPanel({ orgId }: Props) {
  const qc = useQueryClient();

  const agreementQ = useQuery({
    queryKey: ['admin-commercial-agreement', orgId],
    queryFn: () => commercialAgreementApi.get(orgId),
  });

  const plansQ = useQuery({
    queryKey: ['license-plans', 'jumpstart'],
    queryFn: () => licensingApi.listPlans('jumpstart'),
  });

  const agreement = agreementQ.data;
  const plans = plansQ.data ?? [];

  const [planCode, setPlanCode] = useState('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [listNis, setListNis] = useState('');
  const [agreedNis, setAgreedNis] = useState('');
  const [discountType, setDiscountType] = useState<'percent' | 'fixed' | ''>('');
  const [discountValue, setDiscountValue] = useState('');
  const [discountEndsAt, setDiscountEndsAt] = useState('');
  const [billingStartedAt, setBillingStartedAt] = useState('');
  const [firstMonthFree, setFirstMonthFree] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!agreement?.ok) return;
    setPlanCode(agreement.plan_code ?? '');
    setBillingCycle(agreement.billing_cycle === 'yearly' ? 'yearly' : 'monthly');
    setListNis(commercialAgreementApi.agorotToNisInput(agreement.list_price_agorot));
    setAgreedNis(commercialAgreementApi.agorotToNisInput(agreement.agreed_price_agorot));
    setDiscountType((agreement.discount_type as 'percent' | 'fixed' | null) ?? '');
    setDiscountValue(
      agreement.discount_value == null
        ? ''
        : agreement.discount_type === 'percent'
          ? String(agreement.discount_value)
          : (Number(agreement.discount_value) / 100).toFixed(2),
    );
    setDiscountEndsAt(toDateTimeLocal(agreement.discount_ends_at));
    setBillingStartedAt(toDateInput(agreement.billing_started_at));
    setFirstMonthFree(Boolean(agreement.first_month_free));
    setReason(agreement.reason ?? '');
  }, [agreement]);

  const catalogList = useMemo(() => {
    const plan = plans.find((p) => p.code === planCode);
    if (!plan) return null;
    return billingCycle === 'yearly'
      ? (plan.price_yearly_agorot ?? (plan.price_monthly_agorot ?? 0) * 10)
      : plan.price_monthly_agorot ?? 0;
  }, [plans, planCode, billingCycle]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const listPriceAgorot = commercialAgreementApi.nisToAgorot(listNis);
      if (listPriceAgorot == null) throw new Error('List price (+VAT) is required');
      const agreedPriceAgorot = commercialAgreementApi.nisToAgorot(agreedNis);

      let discountValueAgorot: number | null = null;
      if (discountType === 'fixed') {
        discountValueAgorot = commercialAgreementApi.nisToAgorot(discountValue);
      } else if (discountType === 'percent') {
        const n = Number(discountValue);
        if (!Number.isFinite(n) || n < 0 || n > 100) throw new Error('Percent must be 0–100');
        discountValueAgorot = Math.round(n);
      }

      return commercialAgreementApi.set(orgId, {
        planCode,
        billingCycle,
        listPriceAgorot,
        agreedPriceAgorot,
        discountType: discountType || null,
        discountValue: discountType ? discountValueAgorot : null,
        discountEndsAt: discountType ? fromDateTimeLocal(discountEndsAt) : null,
        billingStartedAt: billingStartedAt
          ? new Date(`${billingStartedAt}T00:00:00`).toISOString()
          : null,
        firstMonthFree,
        reason: reason.trim() || null,
      });
    },
    onSuccess: async () => {
      toast.success('הסכם מסחרי נשמר (+מע״מ / net)');
      await qc.invalidateQueries({ queryKey: ['admin-commercial-agreement', orgId] });
      await qc.invalidateQueries({ queryKey: ['admin-org-subs', orgId] });
      await qc.invalidateQueries({ queryKey: ['admin-org-jumpstart-sub', orgId] });
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    },
  });

  if (agreementQ.isLoading) return <Loading text="טוען הסכם מסחרי..." />;

  if (agreementQ.isError) {
    return (
      <div className="card p-4">
        <EmptyState
          icon={<FileText className="h-8 w-8" />}
          title="Commercial agreement unavailable"
          description={
            agreementQ.error instanceof Error
              ? agreementQ.error.message
              : 'admin_get_org_commercial_agreement failed (migration may be pending).'
          }
        />
      </div>
    );
  }

  if (!agreement?.ok) {
    return (
      <div className="card p-4">
        <EmptyState
          icon={<FileText className="h-8 w-8" />}
          title="No JumpStart subscription"
          description="Create a JumpStart license first, then edit the commercial agreement here."
        />
      </div>
    );
  }

  return (
    <div className="card p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-primary">הסכם מסחרי (JumpStart)</h3>
          <p className="text-xs text-ink-secondary mt-1">
            כל המחירים ב־₪ לפני מע״מ (+VAT). לא ניתן לשמור מחיר כולל מע״מ.
          </p>
        </div>
        {agreement.price_basis && agreement.price_basis !== 'net' && (
          <span className="text-xs px-2 py-1 rounded bg-red-500/15 text-red-300">
            price_basis={agreement.price_basis} — will force net on save
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-ink-secondary mb-1">תוכנית</label>
          <select
            value={planCode}
            onChange={(e) => {
              setPlanCode(e.target.value);
              const plan = plans.find((p) => p.code === e.target.value);
              if (plan) {
                const ag =
                  billingCycle === 'yearly'
                    ? (plan.price_yearly_agorot ?? (plan.price_monthly_agorot ?? 0) * 10)
                    : plan.price_monthly_agorot ?? 0;
                setListNis(commercialAgreementApi.agorotToNisInput(ag));
              }
            }}
            className="w-full px-3 py-2 rounded-lg text-sm text-white"
            style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
          >
            {plans.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name} ({p.code})
              </option>
            ))}
            {!plans.some((p) => p.code === planCode) && planCode && (
              <option value={planCode}>{planCode} (current)</option>
            )}
          </select>
        </div>

        <div>
          <label className="block text-xs text-ink-secondary mb-1">מחזור חיוב</label>
          <select
            value={billingCycle}
            onChange={(e) => setBillingCycle(e.target.value as 'monthly' | 'yearly')}
            className="w-full px-3 py-2 rounded-lg text-sm text-white"
            style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
          >
            <option value="monthly">חודשי</option>
            <option value="yearly">שנתי</option>
          </select>
        </div>

        <Input
          label="מחיר מחירון ₪ (+מע״מ)"
          type="number"
          min="0"
          step="0.01"
          value={listNis}
          onChange={(e) => setListNis(e.target.value)}
        />
        <Input
          label="מחיר מוסכם ₪ (+מע״מ)"
          type="number"
          min="0"
          step="0.01"
          value={agreedNis}
          onChange={(e) => setAgreedNis(e.target.value)}
          placeholder="ריק = מחירון"
        />

        <div>
          <label className="block text-xs text-ink-secondary mb-1">סוג הנחה</label>
          <select
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value as 'percent' | 'fixed' | '')}
            className="w-full px-3 py-2 rounded-lg text-sm text-white"
            style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
          >
            <option value="">ללא</option>
            <option value="fixed">סכום קבוע ₪</option>
            <option value="percent">אחוז</option>
          </select>
        </div>

        <Input
          label={discountType === 'percent' ? 'ערך הנחה (%)' : 'ערך הנחה ₪ (+מע״מ)'}
          type="number"
          min="0"
          step="0.01"
          value={discountValue}
          onChange={(e) => setDiscountValue(e.target.value)}
          disabled={!discountType}
        />

        <div>
          <label className="block text-xs text-ink-secondary mb-1">תום הנחה</label>
          <input
            type="datetime-local"
            value={discountEndsAt}
            onChange={(e) => setDiscountEndsAt(e.target.value)}
            disabled={!discountType}
            className="w-full px-3 py-2 rounded-lg text-sm text-white"
            style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
          />
        </div>

        <Input
          label="תחילת חיוב (billing start)"
          type="date"
          value={billingStartedAt}
          onChange={(e) => setBillingStartedAt(e.target.value)}
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-ink-primary">
        <input
          type="checkbox"
          checked={firstMonthFree}
          onChange={(e) => setFirstMonthFree(e.target.checked)}
        />
        חודש ראשון חינם
      </label>

      <Input
        label="סיבה / הערה"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="למה המחיר המוסכם?"
      />

      {catalogList != null && (
        <p className="text-xs text-ink-secondary">
          מחירון קטלוג לתוכנית: {formatNIS(catalogList)} +מע״מ
          {agreement.agreed_price_agorot != null && (
            <> · מוסכם כעת: {formatNIS(agreement.agreed_price_agorot)} +מע״מ</>
          )}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || !planCode}
        >
          {saveMut.isPending ? 'שומר…' : 'שמור הסכם'}
        </Button>
      </div>

      <HistoryList agreement={agreement} />
    </div>
  );
}

function HistoryList({ agreement }: { agreement: CommercialAgreement }) {
  const rows = agreement.history ?? [];
  if (!rows.length) {
    return (
      <div className="pt-3 border-t border-white/5">
        <h4 className="text-xs font-semibold text-ink-secondary mb-2">היסטוריית שינויים</h4>
        <p className="text-xs text-ink-secondary">אין רשומות עדיין.</p>
      </div>
    );
  }

  return (
    <div className="pt-3 border-t border-white/5">
      <h4 className="text-xs font-semibold text-ink-secondary mb-2">היסטוריית שינויים</h4>
      <ul className="space-y-2 max-h-56 overflow-auto">
        {rows.map((row) => {
          const after =
            row.metadata && typeof row.metadata === 'object' && 'after' in row.metadata
              ? (row.metadata.after as Record<string, unknown>)
              : row.metadata && typeof row.metadata === 'object' && 'after_fields' in row.metadata
                ? (row.metadata.after_fields as Record<string, unknown>)
                : null;
          return (
            <li key={row.id} className="text-xs text-ink-secondary">
              <span className="text-ink-primary">{formatDate(row.created_at)}</span>
              {' · '}
              {row.admin_email ?? '—'}
              {' · '}
              {row.action}
              {after && (
                <span className="block text-[11px] opacity-80 mt-0.5 font-mono">
                  {JSON.stringify(after)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
